import { DownloadLinesheetModal } from '@app/components/orders/DownloadLinesheetModal';
import { DownloadProductInformationModal } from '@app/components/orders/DownloadProductInformationModal';
import { Button } from '@app/components/various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { Download } from '@app/components/various/Icons';
import { getOrderStatus } from '@app/ducks';
import { downloadProductImages } from '@app/ducks/documents';
import { getIsOrderClosed, getIsOrderEditable } from '@app/logic/Orders';
import { useDownloadOrderConfirmation } from '@app/services/hooks/documents/useDownloadOrderConfirmation';
import { useDownloadOrderCsv } from '@app/services/hooks/documents/useDownloadOrderCsv';
import { useDownloadOrderExcel } from '@app/services/hooks/documents/useDownloadOrderExcel';
import { useDownloadProductInfoExcel } from '@app/services/hooks/documents/useDownloadProductInfoExcel';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useAmplitude } from '@app/utils/hooks';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

interface Props {
  order: Order.Open | Order.Closed;
  isDisabled: boolean;
}

export const DownloadActions = ({ order, isDisabled }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['checkout', 'common']);
  const { trackEvent } = useAmplitude();
  const orderStatus = useSelector(getOrderStatus);
  const isOrderEditable = getIsOrderEditable(orderStatus);

  const orderEventPath = isOrderEditable ? 'checkout' : 'checkoutSuccess';
  const { showModal: showLinesheetModal, hideModal: hideLinesheetModal, isOpen: isLinesheetModalOpen } = useModalToggle();
  const { showModal: showProductInfoModal, hideModal: hideProductInfoModal, isOpen: isProductInfoModalOpen } = useModalToggle();

  const { mutate: downloadOrderCsv, isPending: isDownloadOrderCsvLoading } = useDownloadOrderCsv();
  const { mutate: downloadOrderExcel, isPending: isDownloadOrderExcelLoading } = useDownloadOrderExcel();
  const { mutate: downloadOrderConfirmation, isPending: isDownloadOrderConfirmationLoading } = useDownloadOrderConfirmation();
  const { mutate: downloadProductInfoExcel, isPending: isDownloadProductInfoExcelLoading } = useDownloadProductInfoExcel();
  const { order: orderId, orderNumber, status } = order;

  const handleSuccessDownloadLinesheetModal = () => {
    trackEvent({ name: 'downloadAction.button.linesheet.submit', properties: { path: orderEventPath } });
  };

  const handleSuccessProductInfoModal = () => {
    trackEvent({ name: 'downloadAction.button.productInformationXls.submit', properties: { path: orderEventPath } });
  };

  const handleDownloadOrderExcel = React.useCallback(() => {
    trackEvent({ name: 'downloadAction.button.orderDetailsXls.submit', properties: { path: orderEventPath } });

    downloadOrderExcel({
      orderId,
      orderNumber,
    });
  }, [orderId, orderNumber, downloadOrderExcel, orderEventPath, trackEvent]);

  const handleDownloadProductImages = React.useCallback(() => {
    trackEvent({ name: 'downloadAction.button.productImages.submit', properties: { path: 'checkoutSuccess' } });

    dispatch(downloadProductImages({ orderId, orderNumber }));
  }, [dispatch, orderId, orderNumber, trackEvent]);

  const handleDownloadOrderCsv = React.useCallback(() => {
    trackEvent({ name: 'downloadAction.button.orderDetailsCsv.submit', properties: { path: 'checkoutSuccess' } });

    downloadOrderCsv({ orderId, orderNumber });
  }, [trackEvent, downloadOrderCsv, orderId, orderNumber]);

  const handleDownloadOrderConfirmation = React.useCallback(() => {
    trackEvent({ name: 'downloadAction.button.orderConfirmation.submit', properties: { path: 'checkoutSuccess' } });

    downloadOrderConfirmation({ orderId, orderNumber });
  }, [trackEvent, downloadOrderConfirmation, orderId, orderNumber]);

  const handleDownloadProductInfoExcel = React.useCallback(() => {
    trackEvent({ name: 'downloadAction.button.productInformationXls.submit', properties: { path: orderEventPath } });
    downloadProductInfoExcel({
      orderId,
      orderNumber,
    });
  }, [orderId, orderNumber, downloadProductInfoExcel, trackEvent, orderEventPath]);

  const handleShowModal = React.useCallback(() => {
    showLinesheetModal();
  }, [showLinesheetModal]);

  const isProductInfoConfiguratorEnabled = getIsFeatureEnabled('ENABLE_PRODUCT_INFO_FILE_CONFIG');
  const enableConfirmDownloadButton = status === 'inProgress' || status === 'completed';
  const isOrderClosed = getIsOrderClosed(order);
  const isDownloadingAnyDocument =
    isDownloadOrderCsvLoading || isDownloadOrderExcelLoading || isDownloadOrderConfirmationLoading || isDownloadProductInfoExcelLoading;

  const actions = [
    {
      isVisible: true,
      key: 'orderDetailsXls',
      label: t('checkout:download.order_details', { extension: 'xls' }),
      onClick: handleDownloadOrderExcel,
    },
    {
      isVisible: isOrderClosed,
      key: 'orderDetailsCsv',
      label: t('checkout:download.order_details', { extension: 'csv' }),
      onClick: handleDownloadOrderCsv,
    },
    {
      isVisible: enableConfirmDownloadButton,
      key: 'orderConfirmationPdf',
      label: t('checkout:download.order_confirmation', { extension: 'pdf' }),
      onClick: handleDownloadOrderConfirmation,
    },
    {
      isVisible: true,
      key: 'linesheetPdf',
      label: t('checkout:download.linesheet', { extension: 'pdf' }),
      onClick: handleShowModal,
    },
    {
      isVisible: true,
      key: 'productInfoXls',
      label: t('checkout:download.product_info', { extension: 'xls' }),
      onClick: isProductInfoConfiguratorEnabled ? showProductInfoModal : handleDownloadProductInfoExcel,
    },
    {
      isVisible: isOrderClosed,
      key: 'productImagesZip',
      label: t('checkout:download.product_images', { extension: 'zip' }),
      onClick: handleDownloadProductImages,
    },
  ];

  const visibleActions = actions.filter(({ isVisible }) => isVisible);

  return (
    <>
      <Dropdown title={t('common:download')}>
        <DropdownTrigger>
          {({ isOpen, triggerProps }) => (
            <Button
              className="px-4"
              variant="bordered"
              color="secondary"
              size="large"
              aria-label={t('common:download')}
              icon={Download}
              disabled={isDisabled}
              isLoading={isDownloadingAnyDocument}
              isChevronOpen={isOpen}
              {...triggerProps}
            />
          )}
        </DropdownTrigger>
        <DropdownMenu items={visibleActions} />
      </Dropdown>
      <DownloadLinesheetModal
        orderId={order.order}
        orderNumber={order.orderNumber}
        location="select units"
        downloadOnlySelected={false}
        isOpen={isLinesheetModalOpen}
        onClose={hideLinesheetModal}
        onSuccess={handleSuccessDownloadLinesheetModal}
      />
      {isProductInfoConfiguratorEnabled && (
        <DownloadProductInformationModal
          isOpen={isProductInfoModalOpen}
          onClose={hideProductInfoModal}
          onSuccess={handleSuccessProductInfoModal}
        />
      )}
    </>
  );
};
